import { useEffect, useState, useRef } from "react";
import API from "../../services/api";
import Navbar from "../../components/Navbar";
import CreateCouponModal from "./CreateCouponModal";
import EditCouponModal from "./EditCouponModal";

interface Coupon {
  _id: string;
  name: string;
  coupon_code: string;
  start_date: string;
  end_date: string;
  per_user_count: number;
  all_count: number;
  used_count: number;
  status: 'active' | 'inactive' | 'expired';
  created_at: string;
  updated_at: string;
}

interface CouponResponse {
  coupons: Coupon[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

type CouponStatus = 'active' | 'inactive' | 'expired';

export default function CouponManagement() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    const styleId = 'coupon-management-styles';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styleSheet);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchCoupons = async () => {
    const currentRequestId = ++requestIdRef.current;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      let url = `/coupons?page=${page}&limit=${limit}`;

      if (searchTerm) {
        url += `&name=${encodeURIComponent(searchTerm)}`;
      }

      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      if (startDate) {
        url += `&start_date=${startDate}`;
      }

      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      const res = await API.get(url, {
        signal: abortControllerRef.current.signal
      });

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      if (res.data && res.data.success) {
        const data: CouponResponse = res.data.data;
        setCoupons(data.coupons);
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.totalItems);
        setError(null);
      } else {
        setError("Failed to fetch coupons");
      }
    } catch (error: any) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Failed to fetch coupons", error);
      setError(error?.response?.data?.message || "Failed to fetch coupons");
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCoupons();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [page, statusFilter, startDate, endDate]);

  const handleSearch = () => {
    setPage(1);
    fetchCoupons();
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);

    setTimeout(() => {
      fetchCoupons();
    }, 0);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleStatusChange = async (id: string, newStatus: CouponStatus) => {
    try {
      const res = await API.patch(`/coupon/${id}/status`, { status: newStatus });
      if (res.data && res.data.success) {
        fetchCoupons();
      }
    } catch (error: any) {
      console.error("Failed to update status", error);
      alert(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      const res = await API.delete(`/coupon/${id}`);
      if (res.data && res.data.success) {
        fetchCoupons();
      }
    } catch (error: any) {
      console.error("Failed to delete coupon", error);
      alert(error?.response?.data?.message || "Failed to delete coupon");
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setShowEditModal(true);
  };

  const getStatusColor = (status: CouponStatus): string => {
    const colors: Record<CouponStatus, string> = {
      active: '#4caf50',
      inactive: '#ff9800',
      expired: '#f44336'
    };
    return colors[status] || '#666';
  };

  const getStatusBadgeStyle = (status: CouponStatus): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: getStatusColor(status),
    color: '#fff',
    textTransform: 'capitalize' as const,
  });

  const renderPageNumbers = () => {
    if (totalPages === 0) return null;

    const pageNumbers: number[] = [];
    const currentPage = page;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 5; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers.map((pageNum) => (
      <button
        key={pageNum}
        onClick={() => handlePageChange(pageNum)}
        style={{
          ...styles.pageNumberButton,
          ...(pageNum === currentPage ? styles.pageNumberActive : {})
        }}
      >
        {pageNum}
      </button>
    ));
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Coupon Management</h1>
            <p style={styles.subtitle}>Manage your promotional coupons and discounts</p>
          </div>
          <button
            style={styles.createButton}
            onClick={() => setShowCreateModal(true)}
          >
            + Create Coupon
          </button>
        </div>

        <div style={styles.filtersContainer}>
          <div style={styles.filterRow}>
            <div style={styles.searchGroup}>
              <input
                type="text"
                placeholder="Search by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                style={styles.searchInput}
              />
              <button onClick={handleSearch} style={styles.searchButton}>
                🔍 Search
              </button>
              <button onClick={handleClearSearch} style={styles.clearButton}>
                ✕ Clear
              </button>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          <div style={styles.filterRow}>
            <div style={styles.dateGroup}>
              <label style={styles.dateLabel}>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={styles.dateInput}
              />
            </div>
            <div style={styles.dateGroup}>
              <label style={styles.dateLabel}>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={styles.dateInput}
              />
            </div>
            {totalItems > 0 && (
              <span style={styles.totalItems}>
                Total: {totalItems} coupon{totalItems > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {loading && (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading coupons...</p>
          </div>
        )}

        {error && (
          <div style={styles.error}>
            <p>{error}</p>
            <button onClick={fetchCoupons} style={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {coupons.length === 0 ? (
             <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🎫</div>
                <h3>No coupons found</h3>
                <p>Create your first coupon to get started</p>
              </div>
            ) : (
              <>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHeader}>
                        <th style={styles.tableHeaderCell}>Name</th>
                        <th style={styles.tableHeaderCell}>Code</th>
                        <th style={styles.tableHeaderCell}>Valid From</th>
                        <th style={styles.tableHeaderCell}>Valid To</th>
                        <th style={styles.tableHeaderCell}>Per User</th>
                        <th style={styles.tableHeaderCell}>Total</th>
                        <th style={styles.tableHeaderCell}>Used</th>
                        <th style={styles.tableHeaderCell}>Status</th>
                        <th style={styles.tableHeaderCell}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map((coupon) => (
                        <tr key={coupon._id} style={styles.tableRow}>
                          <td style={styles.tableCell}>{coupon.name}</td>
                          <td style={styles.tableCell}>
                            <span style={styles.codeBadge}>
                              {coupon.coupon_code}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            {new Date(coupon.start_date).toLocaleDateString()}
                          </td>
                          <td style={styles.tableCell}>
                            {new Date(coupon.end_date).toLocaleDateString()}
                          </td>
                          <td style={styles.tableCell}>{coupon.per_user_count}</td>
                          <td style={styles.tableCell}>{coupon.all_count}</td>
                          <td style={styles.tableCell}>{coupon.used_count || 0}</td>
                          <td style={styles.tableCell}>
                            <span style={getStatusBadgeStyle(coupon.status)}>
                              {coupon.status}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={styles.actionButtons}>
                              <button
                                onClick={() => handleStatusChange(
                                  coupon._id,
                                  coupon.status === 'active' ? 'inactive' : 'active'
                                )}
                                style={styles.actionButton}
                                title={coupon.status === 'active' ? 'Deactivate' : 'Activate'}
                              >
                                {coupon.status === 'active' ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => handleEdit(coupon)}
                                style={styles.actionButton}
                                title="Edit"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(coupon._id)}
                                style={{ ...styles.actionButton, ...styles.deleteButton }}
                                title="Delete"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      style={{
                        ...styles.pageButton,
                        ...(page === 1 ? styles.pageButtonDisabled : {})
                      }}
                    >
                      ← Previous
                    </button>

                    <div style={styles.pageNumbers}>
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      style={{
                        ...styles.pageButton,
                        ...(page === totalPages ? styles.pageButtonDisabled : {})
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <CreateCouponModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchCoupons();
          }}
        />
      )}

      {showEditModal && selectedCoupon && (
        <EditCouponModal
          coupon={selectedCoupon}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCoupon(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCoupon(null);
            fetchCoupons();
          }}
        />
      )}
    </>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a2332",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  createButton: {
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  filtersContainer: {
    backgroundColor: "#fff",
    padding: "16px 20px",
    borderRadius: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "24px",
  },
  filterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginTop: "24px",
  },
  searchGroup: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    flex: 1,
  },
  searchInput: {
    padding: "8px 14px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    minWidth: "200px",
    flex: 1,
  },
  searchButton: {
    padding: "8px 16px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  clearButton: {
    padding: "8px 16px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterLabel: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
  },
  filterSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
  },
  dateGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  dateLabel: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
  },
  dateInput: {
    padding: "6px 10px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    fontSize: "13px",
    outline: "none",
    minWidth: "130px",
  },
  totalItems: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#666",
    marginLeft: "auto",
  },
  loading: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    color: "#666",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #6c5ce7",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  error: {
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "8px",
    padding: "16px 20px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#d32f2f",
  },
  retryButton: {
    padding: "8px 20px",
    backgroundColor: "#f44336",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "60px 20px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    border: "1px dashed #ddd",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  emptyCreateButton: {
    marginTop: "16px",
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    overflow: "auto",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "20px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    minWidth: "1000px",
  },
  tableHeader: {
    backgroundColor: "#f8f9fa",
    borderBottom: "2px solid #e8ecf1",
  },
  tableHeaderCell: {
    padding: "12px 16px",
    textAlign: "left" as const,
    fontSize: "13px",
    fontWeight: "600",
    color: "#555",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "background-color 0.2s",
  },
  tableCell: {
    padding: "12px 16px",
    fontSize: "14px",
    color: "#333",
  },
  codeBadge: {
    display: "inline-block",
    padding: "4px 10px",
    backgroundColor: "#f0f2f5",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#6c5ce7",
    fontFamily: "monospace",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
  },
  actionButton: {
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f2f5",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "#555",
    transition: "all 0.2s",
  },
  deleteButton: {
    color: "#f44336",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "16px 0",
    flexWrap: "wrap",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    transition: "all 0.2s",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  pageNumbers: {
    display: "flex",
    gap: "4px",
  },
  pageNumberButton: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    transition: "all 0.2s",
  },
  pageNumberActive: {
    backgroundColor: "#6c5ce7",
    borderColor: "#6c5ce7",
    color: "#fff",
  },
};